import lazyframe from "lazyframe";

// Delay loading the lazy frame to avoid render blocking time
setTimeout(() => {
  lazyframe(".hompage-banner__video-iframe", {
    debounce: 500,
    lazyload: true,
    autoplay: true,
  });
}, 500);

function ensureYoutubeAPIReady() {
  return new Promise((resolve, reject) => {
    if (window.YT && window.YT.Player) {
      return resolve();
    }

    let otherYoutubeReadyCb = null;
    if (typeof window.onYouTubeIframeAPIReady !== "undefined") {
      otherYoutubeReadyCb = window.onYouTubeIframeAPIReady;
    }

    window.onYouTubeIframeAPIReady = () => {
      otherYoutubeReadyCb && otherYoutubeReadyCb();
      resolve();
    };

    let script = document.createElement("script");
    script.id = "youtube-iframe-script";
    script.src = "https://www.youtube.com/iframe_api";
    let firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
  });
}

function ensureVimeoAPIReady() {
  return new Promise((resolve, reject) => {
    if (window.Vimeo && window.Vimeo.Player) {
      return resolve();
    }

    let script = document.createElement("script");
    script.id = "vimeo-iframe-script";
    script.src = "https://player.vimeo.com/api/player.js";

    // Set up onload event handler
    script.onload = () => {
      // Check if the necessary objects are now available
      if (window.Vimeo && window.Vimeo.Player) {
        resolve();
      } else {
        reject(new Error("Vimeo API script loaded, but Vimeo or Vimeo.Player is still not available"));
      }
    };

    // Set up onerror event handler
    script.onerror = () => {
      reject(new Error("Error loading Vimeo API script"));
    };

    let firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
  });
}

const removePosterImage = (video) => {
  const poster = video.querySelector(".hompage-banner__background-image");

  if (poster) {
    poster.style.opacity = 0;
  }
};

const handleIframeVideos = () => {
  const videoCollection = document.getElementsByClassName("hompage-banner__background-video-wrapper");

  if (videoCollection) {
    const videoArray = Array.from(videoCollection);

    videoArray.forEach((videoItem) => {
      // handle local videos
      if (videoItem.dataset.videoType === "video-local") {
        const frame = videoItem.getElementsByTagName("video");
        frame[0].addEventListener("play", () => {
          removePosterImage(videoItem);
        });
      }

      // handle youtube videos
      if (videoItem.dataset.videoType === "video-youtube") {
        ensureYoutubeAPIReady().then(() => {
          videoArray.forEach((video, i) => {
            // events if youtube video is loaded
            const frame = video.getElementsByTagName("iframe");
            if (frame.length) {
              const player = new YT.Player(frame[0], {
                events: {
                  onReady: () => {
                    isPlayerReady = true;
                  },
                  onStateChange: (e) => {
                    if (e.target.getPlayerState() === 1) {
                      removePosterImage(video);
                    }
                    if (e.target.getPlayerState() === 0) {
                      e.target.playVideo();
                    }
                  },
                },
              });
            }
          });
        });
      }

      // handle vimeo videos
      if (videoItem.dataset.videoType === "video-vimeo") {
        ensureVimeoAPIReady().then(() => {
          videoArray.forEach((video, i) => {
            // events if Vimeo video is loaded
            const frame = video.getElementsByTagName("iframe");

            if (frame.length) {
              if (video.dataset.videoType === "video-vimeo") {
                const player = new Vimeo.Player(frame[0]);

                player.on("play", () => {
                  removePosterImage(video);
                });
              }
            }
          });
        });
      }
    });
  }
};

if (document.readyState !== "loading") {
  handleIframeVideos();
} else {
  document.addEventListener("DOMContentLoaded", handleIframeVideos);
}

// Mobile detection function
function isMobileDevice() {
  return window.innerWidth <= 768 || 
         /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         window.innerWidth <= 454; // Specific breakpoint where issue occurs
}

class MarqueeScroller {
  constructor(element) {
    this.track = element;
    this.hasAnimation = element.dataset.hasAnimation === "true";
    this.hasSeparator = element.dataset.hasSeparator === "true";
    this.heading = element.querySelector(".hompage-banner__heading");
    this.originalContent = this.heading.innerHTML;
    this.cloneObserver = null;
    this.intersectionObserver = null;
    this.fallbackTimer = null;
    this.isMobile = isMobileDevice();
    this.animationStarted = false; // Track if animation has started

    this.init();
  }

  init() {
    if (this.hasAnimation && this.hasSeparator) {
      this.heading.insertAdjacentHTML("afterbegin", '<span class="separator-dot"></span>');
    }

    const initialClones = 4;
    for (let i = 0; i < initialClones; i++) {
      this.appendClone();
    }

    this.setupTrackObserver();
    
    // Add fallback for mobile devices - force animation after 1 second on mobile, 2 seconds on desktop
    const fallbackDelay = this.isMobile ? 1000 : 2000;
    this.fallbackTimer = setTimeout(() => {
      if (!this.track.classList.contains("is-animated")) {
        this.startAnimation();
      }
    }, fallbackDelay);
  }

  startAnimation() {
    if (!this.animationStarted) {
      this.track.classList.add("is-animated");
      this.animationStarted = true;
      
      // Clear the fallback timer
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer);
        this.fallbackTimer = null;
      }
    }
  }

  appendClone() {
    const clone = this.heading.cloneNode(true);
    this.track.appendChild(clone);
    this.observeClone(clone);
  }

  observeClone(clone) {
    if (!this.cloneObserver) {
      this.cloneObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.boundingClientRect.right < 0) {
              this.cloneObserver.unobserve(entry.target);
              entry.target.remove();
              this.appendClone();
            }
          });
        },
        {
          root: null,
          threshold: 0,
        }
      );
    }

    this.cloneObserver.observe(clone);
  }

  setupTrackObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.animationStarted) {
            this.startAnimation();
          }
          // Don't remove the animation class once it's started
          // This prevents the animation from stopping when the element slightly exits the viewport
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -10% 0px" // Add some margin to make it less sensitive
      }
    );

    this.intersectionObserver.observe(this.track);
  }
}

function initMarqueeScrollers() {
  const marqueeElements = document.querySelectorAll("[data-marquee]");

  if (!marqueeElements.length) return;

  marqueeElements.forEach((element) => {
    new MarqueeScroller(element);
  });
}

if (document.readyState !== "loading") {
  initMarqueeScrollers();
} else {
  document.addEventListener("DOMContentLoaded", () => {
    initMarqueeScrollers();
  });
}
